import React, { useEffect, useRef, useState } from 'react';
import { styles } from './ProductGrid.css';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { HeadingDivider } from '../HeadingDivider/HeadingDivider';
import { ProductFallback, ProductFallbackProps } from '../ProductFallback/ProductFallback';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { useIsOnScreen } from '../hooks/useIsOnScreen/useIsOnScreen';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '../Algolia/transformers/transformAlgoliaHitForTracking';
import { UseProductsProps, useProducts, filterUniqueProducts } from '../hooks/useProducts/useProducts';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { NoResultsPlaceholder } from '../NoResultsPlaceholder/NoResultsPlaceholder';
import { ProductCardProps } from '../ProductCard/types';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';

const DEFAULT_PRODUCTS_PER_PAGE = 12;

export type ProductGridProps = {
  heading?: string;
  className?: string;
  renderProduct: (data: { hit: AlgoliaProductHit; position: number }) => React.ReactNode;
  productCardSkeletonProps?: ProductFallbackProps['productCardSkeletonProps'];
  shouldTrackImpressions?: boolean;
  trackingListName?: string;
  productsPerPage?: number;
  productCardVariant?: ProductCardProps['variant'];
} & UseProductsProps;

export const ProductGrid = ({
  heading,
  className,
  renderProduct,
  productCardSkeletonProps = {},
  shouldTrackImpressions = false,
  trackingListName,
  productsPerPage = DEFAULT_PRODUCTS_PER_PAGE,
  productCardVariant = 'tablet',
  ...useProductsProps
}: ProductGridProps) => {
  const [page, setPage] = useState(0);
  const [loadedProducts, setLoadedProducts] = useState<Array<AlgoliaProductHit>>([]);
  const {
    products = [],
    numberOfProducts,
    isLoading,
  } = useProducts({
    algoliaSearchOptions: {
      hitsPerPage: productsPerPage,
      page,
    },
    ...useProductsProps,
  });
  const hasNoProducts = !isLoading && !products?.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useIsOnScreen(containerRef, { persistOnceSeen: true });

  useEffect(() => {
    if (!isLoading && products?.length) {
      setLoadedProducts(prev => filterUniqueProducts(...prev, ...products));
    }
  }, [isLoading, products?.reduce((acc, product) => `${acc}-${product.sku}`, '') ?? '']);

  useEffect(() => {
    if (!isOnScreen || isLoading) return;

    trackViewItemList({
      currency: getStoreCurrency(),
      listName: trackingListName,
      viewedItems: transformAlgoliaHitForTracking(products),
    });

    if (shouldTrackImpressions && products.length) {
      window?.gtm?.track?.('productImpression', {
        products: products.map((product, i) => ({
          ...product.google_tag_manager,
          list: trackingListName,
          position: page * productsPerPage + i + 1,
        })),
      });
    }
  }, [isOnScreen, isLoading]);

  if (hasNoProducts) {
    return (
      <NoResultsPlaceholder
        className={className}
        icon="LecternIllustrated"
        title="Looks like this shelf is empty"
        contents="We’re restocking this section... in the meantime check out our other deals."
      />
    );
  }

  return (
    <div className={classNames(styles.container, className)} ref={containerRef}>
      {heading ? (
        <div className={styles.headingContainer}>{heading ? <HeadingDivider heading={heading} /> : null}</div>
      ) : null}

      <div className={styles.products({ productCardVariant })}>
        {(isLoading ? Array.from(Array(productsPerPage)).fill(null) : loadedProducts).map((product, i) => (
          <ProductFallback
            isLoading={isLoading}
            product={product}
            key={`${i}`}
            productCardSkeletonProps={{ variant: 'tablet', ...productCardSkeletonProps }}
          >
            {product ? renderProduct({ hit: product, position: i + 1 }) : null}
          </ProductFallback>
        ))}
      </div>
      {!isLoading ? (
        <div className={styles.bottomSection}>
          <ProgressBar
            currentItemsCount={loadedProducts.length}
            totalItemsCount={numberOfProducts}
            shouldShowText
            className={styles.progressBar}
          />
          {loadedProducts.length < numberOfProducts ? (
            <Button
              classes={styles.loadMoreButton}
              type="primary"
              label="Load more"
              onClick={() => setPage(prev => prev + 1)}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
